import { ArrowRight, Scale, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useComparison } from '../hooks/ComparisonContext';
import { tx } from '../i18n';

/**
 * The comparison list, always in view once it holds a home.
 *
 * <p>Pinned to the bottom of the screen on every page except the comparison itself, so a tenant who
 * adds homes from the address list can see them collect, take one out again, and knows where to
 * go to compare. The free slot count sits next to the list it limits.
 */
export default function CompareTray() {
  const { comparison, remove } = useComparison();
  const location = useLocation();

  const rows = comparison?.rows ?? [];
  if (rows.length === 0 || location.pathname.startsWith('/compare')) {
    return null;
  }

  const paymentsRequired = comparison?.paymentsRequired ?? true;
  const freeLimit = comparison?.freeLimit ?? 3;
  const used = comparison?.usedFreeSlots ?? 0;
  // Empty slots only mean something while the free limit applies.
  const emptySlots = paymentsRequired ? Math.max(0, freeLimit - used) : 0;

  return (
    <aside className="compare-tray" aria-label={tx('Your comparison list', 'Karşılaştırma listeniz',
    'Twoja lista porównania',
    'A sua lista de comparação',
    'Tu lista de comparación')}>
      <div className="compare-tray__inner">
        <div className="compare-tray__title">
          <Scale size={16} aria-hidden="true" />
          <span>
            <strong>{tx('Comparison', 'Karşılaştırma',
    'Porównanie',
    'Comparação',
    'Comparación')}</strong>
            <span className="compare-tray__count">
              {paymentsRequired
                ? tx(`${used}/${freeLimit} free`, `${used}/${freeLimit} ücretsiz`,
    `${used}/${freeLimit} bezpłatnie`,
    `${used}/${freeLimit} grátis`,
    `${used}/${freeLimit} gratis`)
                : tx(`${rows.length} home${rows.length === 1 ? '' : 's'}`, `${rows.length} konut`,
    `${rows.length} ${rows.length === 1 ? 'mieszkanie' : 'mieszkań'}`,
    `${rows.length} casa${rows.length === 1 ? '' : 's'}`,
    `${rows.length} vivienda${rows.length === 1 ? '' : 's'}`)}
            </span>
          </span>
        </div>

        <ul className="compare-tray__list">
          {rows.map((row) => (
            <li key={row.propertyAddressId} className="compare-tray__chip">
              <span className="compare-tray__chip-text" title={row.addressLine}>
                {shortAddress(row.addressLine)}
              </span>
              <button
                type="button"
                className="compare-tray__remove"
                onClick={() => {
                  void remove(row.propertyAddressId);
                }}
                aria-label={tx(`Remove ${row.addressLine} from the comparison`, `${row.addressLine} adresini karşılaştırmadan çıkar`,
    `Usuń ${row.addressLine} z porównania`,
    `Remover ${row.addressLine} da comparação`,
    `Quitar ${row.addressLine} de la comparación`)}
              >
                <X size={13} aria-hidden="true" />
              </button>
            </li>
          ))}
          {Array.from({ length: emptySlots }, (_, index) => (
            <li key={`empty-${index}`} className="compare-tray__chip compare-tray__chip--empty">
              {tx('Empty slot', 'Boş yer',
    'Wolne miejsce',
    'Lugar vazio',
    'Plaza libre')}
            </li>
          ))}
        </ul>

        <Link to="/compare" className="button button--small compare-tray__go">
          {rows.length === 1
            ? tx('View comparison', 'Karşılaştırmayı gör',
    'Zobacz porównanie',
    'Ver a comparação',
    'Ver la comparación')
            : tx(`Compare ${rows.length} homes`, `${rows.length} konutu karşılaştır`,
    `Porównaj ${rows.length} mieszkań`,
    `Comparar ${rows.length} casas`,
    `Comparar ${rows.length} viviendas`)}
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
      </div>
    </aside>
  );
}

/** "Flat 1, 24 Brune Street, London, E1 6AN" becomes "Flat 1, 24 Brune Street". */
function shortAddress(addressLine: string): string {
  const parts = addressLine.split(',').map((part) => part.trim());
  const withoutPostcodeAndTown = parts.length > 2 ? parts.slice(0, -2) : parts;
  return withoutPostcodeAndTown.join(', ');
}
